import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  MessageSquare,
  Heart,
  TrendingUp,
  Lock,
  LogOut,
  ArrowLeft,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE_LABELS = {
  humour: "Humour",
  sarcasme: "Sarcasme",
  ferme: "Ferme",
  empathique: "Empathique",
  diplomate: "Diplomate",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("psf_admin_token") || "");
  const [input, setInput] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = async (t) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/stats`, {
        headers: { "X-Admin-Token": t },
      });
      setStats(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Token invalide");
        sessionStorage.removeItem("psf_admin_token");
        setToken("");
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchStats(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { token: input });
      sessionStorage.setItem("psf_admin_token", input);
      setToken(input);
      setInput("");
      toast.success("Connecté");
    } catch {
      toast.error("Token incorrect");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("psf_admin_token");
    setToken("");
    setStats(null);
  };

  // ── Login screen ────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF9] bg-grain px-4">
        <form
          onSubmit={login}
          className="nb-card p-8 w-full max-w-md space-y-5"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3">
            <span className="nb-border bg-[#FF4D85] p-2">
              <Lock size={20} strokeWidth={3} />
            </span>
            <h1 className="font-display font-black text-2xl">Espace Admin</h1>
          </div>
          <p className="text-sm font-medium text-[#0A0A0A]/70">
            Entre le token administrateur pour accéder aux statistiques d'usage.
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Token admin"
            className="w-full bg-white nb-border p-3 font-medium focus:outline-none focus:shadow-[4px_4px_0_0_#FF4D85] transition-all"
            autoFocus
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            className="w-full nb-border bg-[#FF4D85] px-6 py-3 font-black nb-shadow hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_#0A0A0A] transition-all"
          >
            Entrer
          </button>
          <Link
            to="/"
            className="block text-center text-sm font-bold underline opacity-70 hover:opacity-100"
          >
            ← Retour à l'app
          </Link>
        </form>
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF9]">
        <p className="font-display font-black text-xl animate-pulse">Chargement des stats…</p>
      </div>
    );
  }

  const t = stats.totals;

  return (
    <div className="min-h-screen bg-[#FFFDF9] bg-grain">
      <header className="sticky top-0 z-40 bg-[#FFFDF9] border-b-2 border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-back-link"
              className="nb-border bg-white px-3 py-2 font-bold text-sm hover:bg-[#A7F3D0] flex items-center gap-2"
            >
              <ArrowLeft size={14} strokeWidth={2.5} /> App
            </Link>
            <span className="font-display font-extrabold text-lg">
              Admin · Merci du conseil
            </span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="nb-border bg-white px-3 py-2 font-bold text-sm hover:bg-[#FF3B30] hover:text-white flex items-center gap-2"
          >
            <LogOut size={14} strokeWidth={2.5} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        <div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            📊 Statistiques d'usage
          </h1>
          <p className="font-medium text-[#0A0A0A]/70 mt-1">
            Vue temps réel des sessions, générations et favoris
          </p>
        </div>

        {/* KPI cards */}
        <section
          data-testid="kpi-grid"
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
        >
          <KpiCard
            testid="kpi-sessions"
            icon={Users}
            label="Sessions uniques"
            value={t.sessions}
            bg="#FFD6E8"
          />
          <KpiCard
            testid="kpi-generations"
            icon={MessageSquare}
            label="Répliques générées"
            value={t.generations}
            bg="#A7F3D0"
          />
          <KpiCard
            testid="kpi-favorites"
            icon={Heart}
            label="Favoris sauvegardés"
            value={t.favorites}
            bg="#FCD34D"
          />
          <KpiCard
            testid="kpi-conversion"
            icon={TrendingUp}
            label="% sessions avec favori"
            value={`${t.favorite_conversion_pct}%`}
            bg="#FF8A00"
          />
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <KpiCard
            testid="kpi-copies"
            icon={MessageSquare}
            label="Copies presse-papiers"
            value={t.total_copies ?? 0}
            bg="#F3F4F6"
          />
          <KpiCard
            testid="kpi-shares"
            icon={TrendingUp}
            label="Partages sociaux"
            value={t.total_shares ?? 0}
            bg="#C7D2FE"
          />
          <KpiCard
            testid="kpi-avg"
            icon={MessageSquare}
            label="Moy. générations / session"
            value={t.avg_generations_per_session}
            bg="#FFD6E8"
          />
          <KpiCard
            testid="kpi-fav-sessions"
            icon={Heart}
            label="Sessions ayant favorisé"
            value={Math.round(((t.favorite_conversion_pct || 0) * (t.sessions || 0)) / 100)}
            bg="#A7F3D0"
          />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily chart */}
          <section className="nb-card p-5" data-testid="chart-daily">
            <h2 className="font-display font-extrabold text-xl mb-4">
              Générations par jour
            </h2>
            {stats.daily.length === 0 ? (
              <p className="text-sm font-medium opacity-60 py-12 text-center">
                Pas encore de données.
              </p>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={stats.daily} margin={{ left: -10, right: 10 }}>
                    <CartesianGrid stroke="#0A0A0A" strokeDasharray="4 4" opacity={0.15} />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fontWeight: 700 }}
                      tickFormatter={(d) => d.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 11, fontWeight: 700 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        border: "2px solid #0A0A0A",
                        borderRadius: 0,
                        boxShadow: "4px 4px 0 0 #0A0A0A",
                        fontWeight: 700,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#FF4D85"
                      strokeWidth={3}
                      dot={{ fill: "#0A0A0A", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          {/* Tones chart */}
          <section className="nb-card p-5" data-testid="chart-tones">
            <h2 className="font-display font-extrabold text-xl mb-4">
              Tons les plus utilisés
            </h2>
            {stats.tones.length === 0 ? (
              <p className="text-sm font-medium opacity-60 py-12 text-center">
                Pas encore de données.
              </p>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={stats.tones.map((x) => ({
                      ...x,
                      label: TONE_LABELS[x.tone] || x.tone,
                    }))}
                    margin={{ left: -10, right: 10 }}
                  >
                    <CartesianGrid stroke="#0A0A0A" strokeDasharray="4 4" opacity={0.15} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fontWeight: 700 }} />
                    <YAxis tick={{ fontSize: 11, fontWeight: 700 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        border: "2px solid #0A0A0A",
                        borderRadius: 0,
                        boxShadow: "4px 4px 0 0 #0A0A0A",
                        fontWeight: 700,
                      }}
                    />
                    <Bar dataKey="count" fill="#A7F3D0" stroke="#0A0A0A" strokeWidth={2} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        </div>

        {/* Recent advice */}
        <section className="nb-card p-5" data-testid="recent-advice">
          <h2 className="font-display font-extrabold text-xl mb-4">
            Derniers conseils reçus (anonymes)
          </h2>
          {stats.recent_advice.length === 0 ? (
            <p className="text-sm font-medium opacity-60 py-6 text-center">
              Pas encore d'activité.
            </p>
          ) : (
            <ul className="space-y-2">
              {stats.recent_advice.map((r, i) => (
                <li key={i} className="nb-border bg-white p-3 flex flex-wrap items-start gap-2">
                  <span className="nb-border bg-[#FCD34D] px-2 py-0.5 text-xs font-black uppercase shrink-0">
                    {TONE_LABELS[r.tone] || r.tone}
                  </span>
                  <p className="font-bold text-sm flex-1 min-w-[200px]">« {r.advice} »</p>
                  <span className="text-xs opacity-60 font-bold">
                    {new Date(r.created_at).toLocaleString("fr-FR")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Top categories + shares */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="nb-card p-5" data-testid="top-categories">
            <h2 className="font-display font-extrabold text-xl mb-4">
              Catégories les plus consultées
            </h2>
            {(!stats.top_categories || stats.top_categories.length === 0) ? (
              <p className="text-sm font-medium opacity-60 py-6 text-center">
                Aucun clic catégorie pour l'instant.
              </p>
            ) : (
              <ul className="space-y-2">
                {stats.top_categories.map((c, i) => {
                  const max = stats.top_categories[0].count || 1;
                  const pct = Math.round((c.count / max) * 100);
                  return (
                    <li
                      key={c.category_id}
                      data-testid={`top-cat-${c.category_id}`}
                      className="flex items-center gap-3"
                    >
                      <span className="nb-border bg-white px-2 py-0.5 text-xs font-black uppercase shrink-0 w-24 truncate">
                        #{i + 1} {c.category_id}
                      </span>
                      <div className="flex-1 nb-border bg-white h-7 relative">
                        <div
                          className="absolute inset-y-0 left-0 bg-[#FF4D85]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="font-black text-sm w-10 text-right">{c.count}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="nb-card p-5" data-testid="share-channels">
            <h2 className="font-display font-extrabold text-xl mb-4">
              Partages par canal
            </h2>
            {(!stats.shares_by_channel || stats.shares_by_channel.length === 0) ? (
              <p className="text-sm font-medium opacity-60 py-6 text-center">
                Aucun partage social encore.
              </p>
            ) : (
              <ul className="space-y-2">
                {stats.shares_by_channel.map((s) => (
                  <li
                    key={s.channel}
                    className="flex items-center justify-between nb-border bg-white p-3"
                  >
                    <span className="font-black uppercase text-sm">{s.channel}</span>
                    <span className="font-display font-black text-2xl">{s.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <p className="text-xs opacity-60 font-bold pb-8">
          Moyenne : {t.avg_generations_per_session} générations / session.
        </p>
      </main>
    </div>
  );
}

function KpiCard({ testid, icon: Icon, label, value, bg }) {
  return (
    <div data-testid={testid} className="nb-card p-5" style={{ background: bg }}>
      <Icon size={22} strokeWidth={2.5} />
      <p className="text-xs font-black uppercase tracking-wider mt-3">{label}</p>
      <p className="font-display font-black text-3xl mt-1">{value}</p>
    </div>
  );
}
